import React, { useState, useEffect } from 'react';
import Button from '../components/ui/Button';
import TacticalCard from '../components/ui/TacticalCard';
import MetricTile from '../components/milsim/MetricTile';
import SectionHeader from '../components/layout/SectionHeader';
import TacticalHero from '../components/hero/TacticalHero';
import NewsSection from '../components/news/NewsSection';
import { GROUND_RANKS, AIR_RANKS } from '../config/ranks';

// Import images
import nightTrainingImg from '../assets/images/nighttraining.jpg';
import cratosImg from '../assets/images/USML_CRATOS2.png';
import hadesImg from '../assets/images/USML_HADES.png';
import sableImg from '../assets/images/USML_SABLE.png';

// Gallery images
import kivuImg1 from '../assets/images/grid/Kivu 1.jpg';
import kivuImg2 from '../assets/images/grid/Kivu 2.jpg';
import napfImg1 from '../assets/images/grid/Napf 1 square.png';
import napfImg2 from '../assets/images/grid/Napf 2 square.jpg';
import rosarioImg1 from '../assets/images/grid/Rosario 1 square.jpg';
import rosarioImg2 from '../assets/images/grid/Rosario 2 square.jpg';
import escudoImg1 from '../assets/images/grid/Escudo de Paz 1 square.jpg';
import escudoImg2 from '../assets/images/grid/Escudo de Paz 2 square.jpg';

// Country flags
import flagAR from '../assets/flags/ar.svg';
import flagMX from '../assets/flags/mx.svg';
import flagPE from '../assets/flags/pe.svg';
import flagNI from '../assets/flags/ni.svg';
import flagCO from '../assets/flags/co.svg';
import flagVE from '../assets/flags/ve.svg';
import flagBO from '../assets/flags/bo.svg';
import flagGT from '../assets/flags/gt.svg';
import flagES from '../assets/flags/es.svg';

function LandingPage() {
  const [selectedBranch, setSelectedBranch] = useState('Todos');
  const [membersData, setMembersData] = useState([]);

  // CRITICAL: Preserve member data fetching logic from App.js lines 44-66
  useEffect(() => {
    fetch('https://api.clanusml.net/api/users/public')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setMembersData(data);
        } else {
          console.warn('Members data empty or invalid, using fallback');
          import('../assets/members.json')
            .then(module => setMembersData(module.default))
            .catch(() => setMembersData([]));
        }
      })
      .catch(err => {
        console.error('Error loading members from API:', err);
        import('../assets/members.json')
          .then(module => setMembersData(module.default))
          .catch(() => setMembersData([]));
      });
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Get branch stats
  const getBranchStats = () => {
    if (!Array.isArray(membersData)) return {};
    const branches = {};
    membersData.forEach(member => {
      const branch = member.branch;
      branches[branch] = (branches[branch] || 0) + 1;
    });
    return branches;
  };

  // Get active members organized by rank hierarchy
  const getHierarchyData = () => {
    if (!Array.isArray(membersData)) return { ground: {}, air: {} };
    const activeMembers = membersData.filter(member => member.status);

    const groundHierarchy = {};
    const airHierarchy = {};

    // Agrupar rangos terrestres (Sable, Cratos)
    GROUND_RANKS.forEach(rank => {
      const membersInRank = activeMembers.filter(member =>
        member.rank === rank && (member.branch === 'Sable' || member.branch === 'Cratos')
      );
      if (membersInRank.length > 0) {
        groundHierarchy[rank] = membersInRank;
      }
    });

    // Agrupar rangos aéreos (Hades)
    AIR_RANKS.forEach(rank => {
      const membersInRank = activeMembers.filter(member =>
        member.rank === rank && member.branch === 'Hades'
      );
      if (membersInRank.length > 0) {
        airHierarchy[rank] = membersInRank;
      }
    });

    return { ground: groundHierarchy, air: airHierarchy };
  };

  return (
    <div className="landing-page">
      {/* Tactical Hero Section */}
      <TacticalHero onNavigate={scrollToSection} />

      {/* About Section */}
      <section id="about" className="about section-padding">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Sobre la <span className="text-accent">USML</span></h2>
              <p>
                La Unidad de Simulación Militar Latina representa la excelencia en operaciones milsim. Fundada desde Arma 2 y consolidada en Arma 3, tiene su base fundamental en los principios de hermandad, disciplina, superioridad táctica y estratégica. Operamos como una fuerza de elite a través de múltiples plataformas de simulación militar.
              </p>
              <p>
                Nuestros miembros pasan por entrenamientos de calidad y mantienen los más altos estándares de preparación para combates en simulación militar. La experiencia acumulada en distintos ambientes de combate durante todos estos años nos ha otorgado una capacidad operativa única para la adaptación inmediata de nuestras unidades en cualquier área de operaciones.
              </p>
              <p>
                USML entrega resultados a través de ejecución precisa y trabajo en equipo inquebrantable. Ya sea participando en operaciones a distintos niveles y escalas o en misiones especializadas, nuestra comunidad está lista para enfrentar cualquier desafío con excelencia táctica y disciplina militar.
              </p>
            </div>
            <div className="about-image">
              <img src={nightTrainingImg} alt="USML Night Training Operations" />
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <NewsSection />

      {/* Branches Section */}
      <section id="branches" className="features section-padding">
        <div className="container">
          <SectionHeader
            title="Ramas operativas de la USML"
            subtitle="Nuestras divisiones especializadas, cada una con sus propias tradiciones y expertise"
            accent="USML"
            centered={true}
          />
          <div className="branches-grid">
            <div className="branch-card">
              <div className="branch-patch">
                <img src={sableImg} alt="Rama Sable" />
              </div>
              <h3>SABLE</h3>
              <p>Infantería Aerotransportada especializada en paracaidismo, columna vertebral de USML en el área de operaciones. Fundada sobre los principios del 75.º Regimiento Ranger, es la infantería de despliegue rápido con entrenamiento completo en especializaciones de mayor requerimiento y profundidad. Conformada por personal con experiencia probada y trayectoria en el clan.</p>
              <div className="branch-stats">
                <span>{getBranchStats()['Sable'] || 0} miembros</span>
              </div>
            </div>
            <div className="branch-card">
              <div className="branch-patch">
                <img src={hadesImg} alt="Rama Hades" />
              </div>
              <h3>HADES</h3>
              <p>Fuerza de apoyo aéreo elite de USML. Dominan los cielos con precisión y velocidad, conformados por pilotos con certificación completa en ala fija y rotativa. Especialistas en operaciones aéreas que operan roles de transporte de tropas, logística y apoyo aéreo cercano a las unidades desplegadas en tierra.</p>
              <div className="branch-stats">
                <span>{getBranchStats()['Hades'] || 0} miembros</span>
              </div>
            </div>
            <div className="branch-card">
              <div className="branch-patch">
                <img src={cratosImg} alt="Rama Cratos" />
              </div>
              <h3>CRATOS</h3>
              <p>Infantería de choque, fuerza de apoyo terrestre de USML a disposición de Sable en el área de operaciones. El personal recibe entrenamiento de infantería y desarrolla especializaciones propias dentro de la rama. Con experiencia y dedicación, pueden ascender internamente o progresar hacia SABLE o HADES.</p>
              <div className="branch-stats">
                <span>{getBranchStats()['Cratos'] || 0} miembros</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Using MetricTile */}
      <section id="stats" className="stats section-padding tp-tactical-section">
        <div className="container">
          <SectionHeader
            title="USML en Números"
            subtitle="Nuestra fortaleza se refleja en estos datos"
            accent="Números"
            centered={true}
          />
          <div className="stats-grid tp-stats-enhanced">
            <MetricTile
              value={membersData.length}
              label="Personal Total"
              variant="default"
            />
            <MetricTile
              value={membersData.filter(m => m.status).length}
              label="Miembros activos"
              variant="success"
            />
            <MetricTile
              value="2013"
              label="Fundación"
              variant="default"
            />
            <MetricTile
              value="9+"
              label="Paises Cooperando"
              variant="default"
              className="tp-metric--countries"
            >
              <div className="country-tile__flags">
                <div className="country-tile__flags-grid">
                  <img src={flagVE} alt="" />
                  <img src={flagMX} alt="" />
                  <img src={flagPE} alt="" />
                  <img src={flagNI} alt="" />
                  <img src={flagBO} alt="" />
                  <img src={flagAR} alt="" />
                  <img src={flagCO} alt="" />
                  <img src={flagGT} alt="" />
                  <img src={flagES} alt="" />
                </div>
              </div>
            </MetricTile>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery section-padding">
        <div className="container">
          <SectionHeader
            title="Galería de Misiones"
            subtitle="Momentos destacados de nuestras operaciones tácticas en varios teatros"
            accent="Misiones"
            centered={true}
          />
          <div className="gallery-grid">
            <div className="gallery-item">
              <img src={kivuImg1} alt="Operación Kivu Alpha" />
              <div className="gallery-overlay">
                <h4>Operación Kivu</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={kivuImg2} alt="Operación Kivu Bravo" />
              <div className="gallery-overlay">
                <h4>Operación Kivu</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={napfImg1} alt="Operación Napf" />
              <div className="gallery-overlay">
                <h4>Operación Napf</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={napfImg2} alt="Evaluación Táctica Napf" />
              <div className="gallery-overlay">
                <h4>Operación Napf</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={rosarioImg1} alt="Campaña Rosario" />
              <div className="gallery-overlay">
                <h4>Campaña Rosario</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={rosarioImg2} alt="Consecuencias Rosario" />
              <div className="gallery-overlay">
                <h4>Campaña Rosario</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={escudoImg1} alt="Escudo de Paz" />
              <div className="gallery-overlay">
                <h4>Operación Escudo de Paz</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={escudoImg2} alt="Operación Escudo de Paz" />
              <div className="gallery-overlay">
                <h4>Operación Escudo de Paz</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Military Hierarchy Section */}
      <section id="members" className="military-hierarchy section-padding">
        <div className="container">
          <SectionHeader
            title="Malla USML"
            subtitle="Estructura jerárquica in-game de miembros en servicio activo"
            accent="USML"
            centered={true}
          />

          {/* Branch Filter */}
          <div className="branch-filter">
            <button
              className={`filter-btn ${selectedBranch === 'Todos' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Todos')}
            >
              Todos ({membersData.filter(m => m.status).length})
            </button>
            <button
              className={`filter-btn ${selectedBranch === 'Sable' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Sable')}
            >
              Sable ({membersData.filter(m => m.status && m.branch === 'Sable').length})
            </button>
            <button
              className={`filter-btn ${selectedBranch === 'Hades' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Hades')}
            >
              Hades ({membersData.filter(m => m.status && m.branch === 'Hades').length})
            </button>
            <button
              className={`filter-btn ${selectedBranch === 'Cratos' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Cratos')}
            >
              Cratos ({membersData.filter(m => m.status && m.branch === 'Cratos').length})
            </button>
          </div>

          {/* Hierarchy Chart - Two Column Layout */}
          <div className="hierarchy-chart-wrapper">
            {/* Ground Forces Column (Sable + Cratos) */}
            {selectedBranch !== 'Hades' && (
            <div className="hierarchy-column">
              <div className="column-header">
                <h3 className="column-title">Malla Terrestre</h3>
                <p className="column-subtitle">Sable & Cratos</p>
              </div>
              <div className="hierarchy-chart">
                {Object.entries(getHierarchyData().ground).map(([rank, members]) => {
                  const filteredMembers = selectedBranch === 'Todos'
                    ? members
                    : members.filter(member => member.branch === selectedBranch);

                  if (filteredMembers.length === 0) return null;

                  return (
                    <div key={rank} className="rank-tier">
                      <div className="rank-header">
                        <h3 className="rank-title">{rank}</h3>
                      </div>
                      <div className="members-row">
                        {filteredMembers.map((member, index) => (
                          <div key={index} className="member-chip">
                            <span className="member-name">{member.name}</span>
                            <span className={`branch-indicator branch-${member.branch.toLowerCase()}`}>
                              {member.branch}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Air Force Column (Hades) */}
            {selectedBranch !== 'Sable' && selectedBranch !== 'Cratos' && (
            <div className="hierarchy-column">
              <div className="column-header">
                <h3 className="column-title">Malla Aérea</h3>
                <p className="column-subtitle">Hades</p>
              </div>
              <div className="hierarchy-chart">
                {Object.entries(getHierarchyData().air).map(([rank, members]) => {
                  const filteredMembers = selectedBranch === 'Todos'
                    ? members
                    : members.filter(member => member.branch === selectedBranch);

                  if (filteredMembers.length === 0) return null;

                  return (
                    <div key={rank} className="rank-tier">
                      <div className="rank-header">
                        <h3 className="rank-title">{rank}</h3>
                      </div>
                      <div className="members-row">
                        {filteredMembers.map((member, index) => (
                          <div key={index} className="member-chip">
                            <span className="member-name">{member.name}</span>
                            <span className={`branch-indicator branch-${member.branch.toLowerCase()}`}>
                              {member.branch}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section - Using TacticalCard */}
      <section id="contact" className="contact section-padding">
        <div className="container">
          <SectionHeader
            title="Únete a la Unidad"
            subtitle="Conectá con nosotros en Discord para comenzar tu proceso de alistamiento."
            accent="Unidad"
            centered={true}
          />

          <div className="contact-content">
            <TacticalCard
              title="Proceso de Reclutamiento"
              variant="default"
            >
              <div className="requirement-item">
                <h4>Requisitos Básicos:</h4>
                <ul>
                  <li>Ser mayor de 18 años</li>
                  <li>Contar con el juego original en steam</li>
                  <li>Contar con micrófono funcional</li>
                  <li>Disponibilidad para los entrenamientos básicos</li>
                  <li>Actitud respetuosa y amigable</li>
                </ul>
              </div>
              <div className="requirement-item">
                <h4>Proceso:</h4>
                <ol>
                  <li>Únete a nuestro Discord</li>
                  <li>Presenta tu solicitud en el canal correspondiente</li>
                  <li>Te contactaremos y tendremos una charla contigo</li>
                  <li>Completa los 4 entrenamientos básicos</li>
                  <li>¡Bienvenido a la USML!</li>
                </ol>
              </div>
            </TacticalCard>

            <div className="discord-widget">
              <h3>Únete a nuestro Discord</h3>
              <p>Servidor oficial de la Unidad de Simulación Militar Latina</p>
              <div className="discord-embed">
                <iframe
                  src="https://discord.com/widget?id=910149518538989588&theme=dark"
                  width="350"
                  height="500"
                  allowtransparency="true"
                  frameBorder="0"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                  title="Discord Server Widget - USML"
                ></iframe>
              </div>
              <div className="discord-backup">
                <Button
                  variant="primary"
                  onClick={() => window.open('https://discord.gg/4PcYVxGrNk', '_blank', 'noopener,noreferrer')}
                >
                  Unirse al Discord
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
